import { InfoCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Input, Table, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useDebounce } from 'use-debounce';
import { MainContent } from '../../../components/MainContent';
import type { AnalysisStationsQuery } from '../../../graphql/generated/graphql.ts';
import { deCollator } from '../../../utils/collator';
import { ANALYSIS_STATIONS_QUERY } from './AnalysisStations.gql';
import { CreateAnalysisStationModal } from './analysis-stations/CreateAnalysisStationModal';
import { UpdateAnalysisStationModal } from './analysis-stations/UpdateAnalysisStationModal';

export type AnalysisStation = NonNullable<AnalysisStationsQuery['labAnalysisStations']>[number];

export const AnalysisStations: React.FC = () => {
  const { token } = theme.useToken();
  const { id: labId } = useParams<{ id: string }>();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editAnalysisStation, setEditAnalysisStation] = useState<AnalysisStation | null>(null);

  const { data, loading, refetch } = useQuery(ANALYSIS_STATIONS_QUERY, {
    variables: {
      labId: labId ?? '',
    },
    pollInterval: 60000,
  });

  const columns: ColumnsType<AnalysisStation> = [
    {
      title: 'Bezeichnung',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 200,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => deCollator.compare(a.name, b.name),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Beschreibung',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      sorter: (a, b) => deCollator.compare(a.description, b.description),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
  ];

  const filteredAnalysisStations: AnalysisStation[] = useMemo(
    () => filterByTokens(data?.labAnalysisStations ?? [], debouncedSearch, [s => s.name, s => s.description]),
    [debouncedSearch, data]
  );

  return (
    <MainContent>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: token.paddingSM }}>
        <Input
          autoFocus
          allowClear
          placeholder="Suche"
          value={search ?? ''}
          onChange={e => setSearch(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            <Tooltip title="Suche nach Bezeichnung oder Beschreibung">
              <InfoCircleOutlined />
            </Tooltip>
          }
          style={{ width: '300px', marginRight: token.paddingSM }}
        />
        <Button type="primary" icon={<PlusCircleOutlined />} disabled={loading} onClick={() => setCreateModalOpen(true)}>
          Neue Analysestation
        </Button>
      </div>
      <Table<AnalysisStation>
        rowKey={record => record.id}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredAnalysisStations}
        onRow={record => ({ onClick: () => setEditAnalysisStation(record), style: { cursor: 'pointer' } })}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Analysestationen`,
        }}
        loading={loading}
        columns={columns}
      />
      <CreateAnalysisStationModal
        open={createModalOpen}
        onSuccess={() => refetch()}
        onClose={() => setCreateModalOpen(false)}
        labId={labId ?? ''}
        analysisStations={data?.labAnalysisStations ?? []}
      />
      <UpdateAnalysisStationModal
        analysisStation={editAnalysisStation}
        onSuccess={() => refetch()}
        onClose={() => setEditAnalysisStation(null)}
        analysisStations={data?.labAnalysisStations ?? []}
      />
    </MainContent>
  );
};
